import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { getProfile, requestLoginCode, verifyLoginCode } from '../services/auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStep, setAuthStep] = useState('email');
  const [pendingEmail, setPendingEmail] = useState('');

  const logout = useCallback(() => {
    localStorage.removeItem('vpn_token');
    setProfile(null);
    setPendingEmail('');
    setAuthStep('email');
  }, []);

  const refreshProfile = useCallback(async () => {
    const data = await getProfile();
    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    const initialize = async () => {
      if (localStorage.getItem('vpn_token')) {
        try {
          await refreshProfile();
        } catch (_) {
          logout();
        }
      }
      setLoading(false);
    };
    initialize();
  }, [logout, refreshProfile]);

  useEffect(() => {
    window.addEventListener('vpn:unauthorized', logout);
    return () => window.removeEventListener('vpn:unauthorized', logout);
  }, [logout]);

  const requestCode = async (email) => {
    const normalized = email.trim().toLowerCase();
    const result = await requestLoginCode(normalized);
    setPendingEmail(normalized);
    setAuthStep('code');
    return result;
  };

  const login = async (code) => {
    const result = await verifyLoginCode(pendingEmail, code);
    localStorage.setItem('vpn_token', result.access_token);
    setProfile(await getProfile());
    setAuthStep('email');
    return result;
  };

  const acceptToken = async (token) => {
    localStorage.setItem('vpn_token', token);
    setProfile(await getProfile());
  };

  const value = {
    user: profile?.user || null,
    profile,
    loading,
    authStep,
    pendingEmail,
    requestCode,
    login,
    acceptToken,
    logout,
    refreshProfile,
    resetAuth: () => setAuthStep('email'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
